import { pushLog } from '../utils/devUtils';
import type { WorkerMessage, ActionType, WorkerResponse } from './type';

export default class GroupEncryptionWorkerManager {
  private worker: Worker;
  private callbacks: Map<string, (data: WorkerResponse) => void>;
  private messageCounter: number = 0;

  constructor() {
    this.worker = new Worker(new URL('./groupEncryptionWorker', import.meta.url), {
      type: 'module',
    });
    this.callbacks = new Map();

    this.worker.onmessage = this.handleWorkerMessage.bind(this);
    this.worker.onerror = this.handleWorkerError.bind(this);
  }

  private handleWorkerMessage(event: MessageEvent<WorkerResponse>): void {
    const { id } = event.data;
    const callback = this.callbacks.get(id);
    if (callback) {
      callback(event.data);
      this.callbacks.delete(id);
    } else {
      console.error(`Callback not found for encryption id: ${id}`);
    }
  }

  private handleWorkerError(error: ErrorEvent): void {
    console.error('Worker error:', error.message);
  }

  sendWorkerMessage<K extends ActionType, T = any>(
    type: K,
    message?: Omit<WorkerMessage, 'id' | 'type'>
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      const id = `${type}_${this.messageCounter++}`;
      const cb = pushLog('db:' + id, {
        ...message,
        type,
      });

      this.callbacks.set(id, (result) => {
        cb?.(result);
        if (result.success) {
          resolve(result.data as T);
        } else {
          console.error('db:error:' + id, {
            ...message,
            type,
            get ____result____() {
              return result;
            },
          });
          reject(new Error(result.error));
        }
      });
      this.worker.postMessage({ ...message, type, id });
    });
  }

  getSendWorker() {
    function send<T = any, K extends ActionType = ActionType>(
      type: K,
      message?: Omit<WorkerMessage, 'id' | 'type'>
    ): Promise<T>;
    function send<T, K extends ActionType = ActionType>(
      this: GroupEncryptionWorkerManager,
      action: K,
      message: any
    ) {
      return this.sendWorkerMessage<K, T>(action, message);
    }
    return send.bind(this);
  }
}
